import React, { useState } from 'react';
import { Keyboard, StyleSheet, View } from 'react-native';

import { Button, Text, TextInput } from '~/components/core';
import config from '~/config';
import { FONT_FAMILY_BOLD, FONT_SIZE_M, FONT_SIZE_S, FONT_SIZE_XXL } from '~/constants';
import { isDesktop, scaleSize } from '~/constants/scale';
import { useAppSelector, useStyles } from '~/hooks';
import { accountBalanceSelector } from '~/state/selectors';
import { Theme } from '~/theme';
import i18n from '~/i18n';
import { linkTo } from '~/navigation';
import paths from '~/navigation/paths';
import CopyRight from '../WithdrawFunds/CopyRight';
import FlowScreen from '../AddFundsShared/FlowScreen';

type Props = {
    setActiveRightTab?: Function;
    setCurrentAmount?: Function;
};

export default function AddFunds({ setActiveRightTab, setCurrentAmount }: Props) {
    const styles = useStyles(stylesGenerator);
    const currency = useAppSelector(accountBalanceSelector)?.currency || '';
    const [amount, setAmount] = useState('');

    function onChangedAmount(text: string) {
        var newText = '';
        var numbers = '0123456789.';
        text = text.replace(',', '.');
        if (text.length < 1) {
            setAmount('');
            if (setCurrentAmount) {
                setCurrentAmount('');
            }
        }
        for (var i = 0; i < text.length; i++) {
            if (numbers.indexOf(text[i]) > -1) {
                newText = newText + text[i];
            }
        }

        setAmount(newText);
        if (setCurrentAmount) {
            setCurrentAmount(newText);
        }
    }

    function goNext() {
        if (!amount || amount === '') {
            return;
        }
        isDesktop() && setActiveRightTab
            ? setActiveRightTab('add-money-category')
            : linkTo(paths.addFundsCategory.replace(':amount', amount));
    }

    return (
        <FlowScreen
            title="deposit-funds"
            goToScreenName="add-money"
            setActiveRightTab={setActiveRightTab}
            footer={<Button title={'account-balance-add-funds'} disabled={!amount || amount === ''} onPress={goNext} />}
        >
            <View style={styles.hero}>
                <Text fontType="LATO_BOLD" style={styles.heroText}>
                    {'add-funds-header-text-part1'}
                </Text>
                <Text fontType="LATO_BOLD" style={styles.heroText}>
                    {i18n.t('add-funds-header-text-part2') + ' ' + config.BRAND_NAME}
                </Text>
            </View>

            <Text style={styles.label}>{i18n.t('deposit-amount') + ' ( ' + currency + ' )'}</Text>
            <TextInput
                placeholder="enter-amount"
                autoCapitalize="none"
                autoCorrect={false}
                value={amount}
                onChangeText={(text) => onChangedAmount(text)}
                keyboardType="decimal-pad"
                maxLength={10}
                autoFocus={true}
                returnKeyType="done"
                onSubmitEditing={() => {
                    Keyboard.dismiss();
                    goNext();
                }}
                containerStyle={styles.amountField}
                style={amount && amount !== '' ? styles.amountText : null}
            />

            <View style={styles.benefits}>
                <Text style={styles.benefitPrimary}>{'add-funds-footer-text1'}</Text>
                <Text style={styles.benefitSecondary}>{'add-funds-footer-text2'}</Text>
            </View>

            <CopyRight />
        </FlowScreen>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        hero: {
            alignItems: 'center',
            paddingBottom: scaleSize(28),
        },
        heroText: {
            fontSize: FONT_SIZE_M,
            color: theme.switch_trackOn,
            textAlign: 'center',
        },
        label: {
            fontSize: FONT_SIZE_S,
            color: theme.textSecondary,
            marginBottom: scaleSize(6),
        },
        amountField: {
            height: scaleSize(56),
            // No alignItems here: it would stop the TextInput stretching, and Android then sizes it
            // from its own under-measured intrinsic height and clips the top and bottom of the digits.
            // Vertical padding moves to the input so it owns the full height.
            paddingVertical: 0,
        },
        amountText: {
            color: theme.tabIconSelected,
            fontFamily: FONT_FAMILY_BOLD,
            letterSpacing: 1,
            fontSize: FONT_SIZE_XXL,
            textAlignVertical: 'center',
        },
        benefits: {
            alignItems: 'center',
            paddingTop: scaleSize(20),
        },
        benefitPrimary: {
            fontSize: FONT_SIZE_S,
            color: theme.switch_trackOn,
            textAlign: 'center',
            marginBottom: scaleSize(6),
        },
        benefitSecondary: {
            fontSize: FONT_SIZE_S,
            color: theme.text_positionsCount,
            textAlign: 'center',
        },
    });
